import {
  faUpRightFromSquare,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Anchor,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
} from '../../../core';
import { type AutoRefreshMode, useAutoRefresh } from './useAutoRefresh';

/** A quote's rating lifecycle, as the agent and fir-account report it. */
export type CreateQuoteStatus =
  | 'REQUESTED'
  | 'READY'
  | 'INDICATION'
  | 'REFERRED'
  | 'DECLINED'
  | 'INVALID';

/** Written to `/results/<row id>` by the client after RefreshQuoteStatus. */
export interface QuoteStatusResult {
  readonly status?: string;
  readonly quoteNumber?: string;
  readonly premium?: string;
  readonly statusReasons?: readonly string[];
  readonly errorReason?: string;
}

/** Same path, written when the refresh itself fails. */
export interface QuoteStatusError {
  readonly error: true;
  readonly message?: string;
}

interface StatusMeta {
  readonly label: string;
  readonly color: string;
  /** One-line explanation under the metrics. */
  readonly note?: string;
  /** Heading for `statusReasons`. */
  readonly reasonsTitle?: string;
  /** No quote could be rated. */
  readonly failed?: true;
}

const STATUS_META: Readonly<Record<CreateQuoteStatus, StatusMeta>> = {
  REQUESTED: { label: 'Rating', color: 'gray' },
  READY: { label: 'Ready', color: 'green' },
  INDICATION: {
    label: 'Indication',
    color: 'blue',
    note: 'Estimated premium — not bindable yet.',
    reasonsTitle: 'Why it is an estimate',
  },
  REFERRED: {
    label: 'Referred',
    color: 'orange',
    note: 'Needs underwriter review before it can bind.',
    reasonsTitle: 'Referral reasons',
  },
  DECLINED: {
    label: 'Declined',
    color: 'red',
    reasonsTitle: 'Decline reasons',
    failed: true,
  },
  INVALID: { label: 'Invalid', color: 'red', failed: true },
};

/** Unknown or missing status reads as still rating, never as a success. */
export function normalizeStatus(value?: string): CreateQuoteStatus {
  const upper = (value ?? '').toUpperCase();
  return upper in STATUS_META ? (upper as CreateQuoteStatus) : 'REQUESTED';
}

function isStatusError(
  value: QuoteStatusResult | QuoteStatusError | undefined
): value is QuoteStatusError {
  return Boolean(value) && (value as QuoteStatusError).error === true;
}

export interface CreateQuoteRowProps {
  readonly label: string;
  readonly limit: string;
  readonly retention: string;
  /** ZSP: the quote's parts and their terms, e.g. 'D&O $1M / $10K · Crime $500K / $5K'. */
  readonly parts?: string;
  /** Formatted premium; empty while rating or when declined. */
  readonly price: string;
  readonly status?: string;
  readonly quoteNumber?: string;
  readonly statusReasons?: readonly string[];
  readonly errorReason?: string;
  /** Re-check on its own while pending; omitted = manual "Check status" only. */
  readonly autoRefresh?: AutoRefreshMode;
  readonly result?: QuoteStatusResult | QuoteStatusError;
  readonly onOpenReview?: () => void;
  readonly onRefresh?: () => void;
}

/**
 * What the row shows. A refresh result only applies while the declared row
 * is still rating; once the agent re-declares it resolved, the props win.
 */
export function resolveRowState(
  props: Pick<
    CreateQuoteRowProps,
    'status' | 'quoteNumber' | 'price' | 'statusReasons' | 'errorReason'
  >,
  result?: QuoteStatusResult | QuoteStatusError
): {
  readonly status: CreateQuoteStatus;
  readonly quoteNumber?: string;
  readonly price: string;
  readonly statusReasons: readonly string[];
  readonly errorReason?: string;
  readonly checkError?: string;
  /** A refresh result replaced the declared props. */
  readonly refreshed?: true;
} {
  const base = {
    status: normalizeStatus(props.status),
    quoteNumber: props.quoteNumber,
    price: props.price,
    statusReasons: props.statusReasons ?? [],
    errorReason: props.errorReason,
  };
  if (isStatusError(result)) {
    return {
      ...base,
      checkError: result.message || "Couldn't check status — try again",
    };
  }
  if (!result?.status || base.status !== 'REQUESTED') return base;
  return {
    status: normalizeStatus(result.status),
    quoteNumber: result.quoteNumber || base.quoteNumber,
    price: result.premium || base.price,
    statusReasons: result.statusReasons ?? base.statusReasons,
    errorReason: result.errorReason ?? base.errorReason,
    refreshed: true,
  };
}

/** Quote number, as a link to the review when `onOpen` is given. */
function QuoteNumber({
  quoteNumber,
  onOpen,
}: Readonly<{ quoteNumber?: string; onOpen?: () => void }>) {
  if (!quoteNumber) return null;
  if (!onOpen) {
    return (
      <Text size="xs" c="dimmed">
        {quoteNumber}
      </Text>
    );
  }
  return (
    <Anchor size="xs" fw={500} onClick={() => onOpen()}>
      <Group gap={4} wrap="nowrap">
        {quoteNumber}
        <FontAwesomeIcon
          icon={faUpRightFromSquare}
          style={{ width: 10, height: 10 }}
        />
      </Group>
    </Anchor>
  );
}

/** One labelled figure; `strong` marks the headline premium. */
function Metric({
  label,
  value,
  strong,
}: Readonly<{ label: string; value: string; strong?: boolean }>) {
  return (
    <Box>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size={strong ? 'md' : 'sm'} fw={strong ? 700 : 500}>
        {value || '—'}
      </Text>
    </Box>
  );
}

/**
 * One quote created from a submission. P100/P250 rows start "Rating" and
 * resolve when the quote's lifecycle event lands (or on "Check status");
 * Px rows arrive already rated.
 */
export function CreateQuoteRow({
  label,
  limit,
  retention,
  parts,
  price,
  status,
  quoteNumber,
  statusReasons,
  errorReason,
  autoRefresh,
  result,
  onOpenReview,
  onRefresh,
}: CreateQuoteRowProps) {
  const view = resolveRowState(
    { status, quoteNumber, price, statusReasons, errorReason },
    result
  );
  const meta = STATUS_META[view.status];
  const pending = view.status === 'REQUESTED';
  const canOpen = Boolean(onOpenReview) && !pending;
  const checkError = view.checkError;
  // A background check came back and the quote is still rating.
  const stillRating = pending && view.refreshed;
  useAutoRefresh(autoRefresh, pending, onRefresh);

  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap" mb="sm">
        <Stack gap={4} style={{ minWidth: 0 }}>
          <Text fw={600} size="sm">
            {label}
          </Text>
          <QuoteNumber
            quoteNumber={view.quoteNumber}
            onOpen={canOpen ? onOpenReview : undefined}
          />
        </Stack>
        <Badge
          color={meta.color}
          variant="light"
          size="sm"
          radius="sm"
          tt="none"
          fw={500}
        >
          {meta.label}
        </Badge>
      </Group>

      <SimpleGrid cols={3} spacing="sm" mb="sm">
        <Metric label="Premium" value={view.price} strong />
        <Metric label="Limit" value={limit} />
        <Metric label="Deductible" value={retention} />
      </SimpleGrid>

      {parts && (
        <Text size="xs" c="dimmed" mt={-4} mb="sm">
          {parts}
        </Text>
      )}

      {pending && (
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
            <Loader size="xs" style={{ flexShrink: 0 }} />
            <Text size="xs" c={checkError ? 'red.6' : 'dimmed'} fs="italic">
              {checkError ??
                (stillRating
                  ? 'Still rating — checking again shortly.'
                  : 'Rating — the premium is not back yet.')}
            </Text>
          </Group>
        </Group>
      )}

      {meta.failed && (
        <Text size="xs" c="red.6">
          {view.errorReason || 'No quote could be rated for these terms.'}
        </Text>
      )}

      {meta.note && (
        <Text size="xs" c="dimmed">
          {meta.note}
        </Text>
      )}

      {meta.reasonsTitle && view.statusReasons.length > 0 && (
        <Box mt="xs">
          <Text size="xs" fw={600} c="dimmed" mb={2}>
            {meta.reasonsTitle}
          </Text>
          <Stack gap={2}>
            {view.statusReasons.map((reason) => (
              <Text key={reason} size="xs" c="dimmed" lh={1.4}>
                {`• ${reason}`}
              </Text>
            ))}
          </Stack>
        </Box>
      )}

      {canOpen && (
        <Button
          variant="light"
          color="blue"
          size="xs"
          mt="sm"
          fullWidth
          leftSection={
            <FontAwesomeIcon
              icon={faUpRightFromSquare}
              style={{ width: 12, height: 12 }}
            />
          }
          onClick={() => onOpenReview?.()}
        >
          Open quote
        </Button>
      )}
    </Paper>
  );
}
